import type {
  HTMLAttributes,
  ReactNode,
  TableHTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react';
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react';
import { cn } from '../lib/utils';

/** DESIGN.md §6 Table: wrapper scrolls horizontally, zebra body, numbers right + tabular. */
export function Table({ className, ...props }: TableHTMLAttributes<HTMLTableElement>) {
  return (
    // `relative`: absolutely positioned content (e.g. `sr-only` header text) stays inside the
    // scroll container instead of widening the page.
    <div className="relative overflow-x-auto">
      <table className={cn('w-full text-sm', className)} {...props} />
    </div>
  );
}

export function THead({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      className={cn('[&>tr]:border-b [&>tr]:border-[var(--line-strong)]', className)}
      {...props}
    />
  );
}

export function TBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn('[&>tr:nth-child(odd)]:bg-black/[0.03]', className)} {...props} />;
}

export function TFoot({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tfoot
      className={cn('border-t border-[var(--line-strong)] font-semibold', className)}
      {...props}
    />
  );
}

type Align = 'left' | 'right' | 'center';
const alignClass: Record<Align, string> = {
  left: 'text-left',
  right: 'text-right tabular-nums',
  center: 'text-center',
};

export function Th({
  align = 'left',
  className,
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & { align?: Align }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-3 py-3 text-xs font-medium whitespace-nowrap text-[var(--ink-soft)]',
        alignClass[align],
        className,
      )}
      {...props}
    />
  );
}

export function Td({
  align = 'left',
  className,
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & { align?: Align }) {
  return <td className={cn('px-3 py-2.5 align-top', alignClass[align], className)} {...props} />;
}

/** Sortable header button: `IconArrowUp/Down size=13` on the active key. */
export function SortButton({
  label,
  direction,
  onClick,
  sortLabel,
}: {
  label: ReactNode;
  direction: 'asc' | 'desc' | false;
  onClick: () => void;
  /** Accessible description, e.g. "Sort by Name". */
  sortLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={sortLabel}
      className="inline-flex items-center gap-1 rounded-md hover:text-[var(--ink)]"
    >
      {label}
      {direction === 'asc' ? <IconArrowUp size={13} stroke={2} aria-hidden /> : null}
      {direction === 'desc' ? <IconArrowDown size={13} stroke={2} aria-hidden /> : null}
    </button>
  );
}

/** Missing value marker. */
export const MISSING = '—';
